import Button from "@/components/atoms/Button";
import { DatePicker } from "@/components/atoms/DatePicker";
import { useForm, UseFormReturn, Controller } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Input from "@/components/atoms/Input";
import AutoInvoiceSchema from "@/schemas/autoInvoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import AutoInvoice from "@/types/AutoInvoice";
import Select from "@/components/molecules/Select";
import { Account } from "@/types/Account";
import Link from "@/components/atoms/Link";
import ErrorType from "@/types/ErrorType";
import { capitalize } from "@/lib/utils";
import type { NestedItem } from "@/types/NestedItem";
import axios from 'axios';
import { FieldValues } from 'react-hook-form';
import { useState } from 'react';
import Spinner from '@/components/atoms/Spinner';
import ForwardedNumericFormat from "@/components/atoms/ForwardedNumericFormat";
import ReasonModal from "@/components/organisms/AutoInvoice/ReasonModal";
import { toUtcDate } from "@/lib/utils";
import * as Constants from "@/constants";

export type CreditNoteFormProps = {
  accounts: Account[];
  entities: NestedItem[];
  nmq_entities: NestedItem[];
  invoice_type: string;
};

type Props = {
  mode?: "create" | "edit";
  autoInvoice: AutoInvoice | null;
  edit: boolean;
  onSubmit: (data: AutoInvoice) => void;
  data: CreditNoteFormProps;
  loading: boolean;
  form: UseFormReturn<any>;
  errors?: ErrorType;
};

interface HandleFieldChangeParams {
  value: string;
  field: FieldValues;
  data: CreditNoteFormProps;
  setData: React.Dispatch<React.SetStateAction<CreditNoteFormProps>>;
}

export const useCreditNoteForm = (autoInvoice: AutoInvoice | null) =>
  useForm({
    resolver: zodResolver(AutoInvoiceSchema),
    defaultValues: autoInvoice
      ? {
          ...autoInvoice,
          account_id: autoInvoice.account.id.toString(),
          entity_id: autoInvoice.entity?.id?.toString() || "",
          nmq_entity_id: autoInvoice.nmq_entity?.id?.toString() || "",
          sow_ids: autoInvoice.sows?.map((sow) => sow.id.toString()),
        }
      : {
          date: new Date(),
          account_id: "",
          entity_id: "",
          nmq_entity_id: "",
          service_period_start: "",
          service_period_end: "",
          sow_ids: [],
          reference_no: "",
          order_ref_no: "",
          payment_terms: "",
          invoicing_queries: "",
          client_contact: "",
          client_address: "",
          client_vat: "",
          exact_customer_id: "",
          client_country: "",
          currency_type: "",
          service_name: "",
          notes: "",
          invoice_type: "CREDIT NOTE",
          calculated_data_html: "",
          additional_type: "ADDITIONAL CHARGE",
          additional_amount_description: "",
          additional_cost_discount: 0,
          total_amount: 0,
          total_sub_amount: 0,
          vat_amount: 0,
          vat_percent: 0,
          actual_total_amount: 0,
          unchecked_columns: [],
          reason: "",
          commit_type: "",
          ev_data: [],
        },
  });

const CreditNoteForm = ({
  form,
  mode = "edit",
  edit,
  onSubmit,
  autoInvoice,
  data,
  loading: initialLoading,
  errors,
}: Props) => {
  const [formData, setFormData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [creditInvoice, setCreditInvoice] = useState<AutoInvoice | null>(null);
  const [creditInvoiceList, setCreditInvoiceList] = useState<any[]>([]);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [reasonType, setReasonType] = useState("credit note");

  const calculateVatPercent = (autoInvoice: AutoInvoice) => {
    const {
      total_sub_amount = 0,
      additional_cost_discount = 0,
      vat_amount = 0,
      additional_type = 'ADDITIONAL CHARGE',
    } = autoInvoice;
  
    let vat_percent = 0;
    let total_amount = 0;
  
    if (vat_amount && vat_amount !== 0) {

      if (additional_type === 'ADDITIONAL CHARGE') {
        total_amount = (total_sub_amount + additional_cost_discount) +
          (total_sub_amount + additional_cost_discount) * (vat_amount / total_sub_amount);
      } else if (additional_type === 'ADDITIONAL DISCOUNT') {
        total_amount = (total_sub_amount - additional_cost_discount) +
          (total_sub_amount - additional_cost_discount) * (vat_amount / total_sub_amount);
      }

      vat_percent = (vat_amount / total_sub_amount) * 100;
    }
  
    return Math.round(vat_percent);
  };

  const [hiddenValues, setHiddenValues] = useState({
    invoice_type: formData.invoice_type,
    vat_percent: autoInvoice ? calculateVatPercent(autoInvoice) : 0,
    actual_total_amount: autoInvoice?.actual_total_amount || 0,
    exact_customer_id: autoInvoice?.exact_customer_id || "",
    client_country: autoInvoice?.client_country || "",
    reason: autoInvoice?.reason || "",
    commit_type: autoInvoice?.commit_type || "",
    total_sub_amount: autoInvoice?.total_sub_amount || 0,
    total_amount: autoInvoice?.total_amount || 0,
    vat_amount: autoInvoice?.vat_amount || 0,
    ev_data: autoInvoice?.ev_data || {},
    calculated_data_html: autoInvoice?.calculated_data_html || "",
    additional_cost_discount: autoInvoice?.additional_cost_discount || 0,
    unchecked_columns: autoInvoice?.unchecked_columns || [],
  });

  const formatDateWithoutTime = (date: string) => {
    const formattedDate = new Date(date);
    return formattedDate.toISOString().split('T')[0];
  };
  
  const handleSubmit = (data: any) => {
    const payload = {
      auto_invoice: {
        ...data,
        calculated_data_html: hiddenValues.calculated_data_html,
        ev_data: null,
        invoice_type: hiddenValues.invoice_type,
        vat_percent: hiddenValues.vat_percent,
        actual_total_amount: hiddenValues.actual_total_amount,
        client_country: hiddenValues.client_country,
        commit_type: hiddenValues.commit_type,
        total_sub_amount: hiddenValues.total_sub_amount,
        total_amount: hiddenValues.total_amount,
        vat_amount: hiddenValues.vat_amount,
        additional_cost_discount: hiddenValues.additional_cost_discount,
        unchecked_columns: hiddenValues.unchecked_columns,
        exact_customer_id: hiddenValues.exact_customer_id,
        service_period_start: formatToISO(data.service_period_start),
        service_period_end: formatToISO(data.service_period_end),
        date: formatToISO(data.date),
      },
    };
 
    onSubmit(payload as any);
  };

  const formatToISO = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
  
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    const adjustedDate = new Date(date.getTime() - timezoneOffsetMs);
  
    return adjustedDate.toISOString();
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  useEffect(() => {
    if (!edit && autoInvoice)
      form.reset({
        ...autoInvoice,
        entity_id: autoInvoice?.entity?.id?.toString() || "",
        nmq_entity_id: autoInvoice?.nmq_entity?.id.toString() || "",
        auto_invoice_id: autoInvoice?.auto_invoice_id || null,
        service_period_start: toUtcDate(autoInvoice.service_period_start),
        service_period_end: toUtcDate(autoInvoice.service_period_end),
        date: toUtcDate(autoInvoice.date),
      });
  }, [edit, autoInvoice]);

  useEffect(() => {
    if (autoInvoice) {
      form.reset({
        ...autoInvoice,
        entity_id: autoInvoice?.entity?.id?.toString() || "",
        nmq_entity_id: autoInvoice?.nmq_entity?.id.toString() || "",
        auto_invoice_id: autoInvoice?.auto_invoice_id || null,
        service_period_start: toUtcDate(autoInvoice.service_period_start),
        service_period_end: toUtcDate(autoInvoice.service_period_end),
        date: toUtcDate(autoInvoice.date),
      });
    }
  }, [autoInvoice]);

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);
  

  async function handleAccountChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/accounts/${value}/read_account`);

    setData({
      ...data,
      entities: response.data.entities,
    });
  }

  async function handleEntityChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    const response = await axios.get(`/entities/${value}/read_entity`);

    setData({
      ...data,
      nmq_entities: response.data.nmq_entities
    });

    fetchInvoices();
  }

  async function handleNMQEntityChange({ value, field, data, setData }: HandleFieldChangeParams) {
    field.onChange(value);
    fetchInvoices();
  }

  async function fetchInvoices() {
    const accountId = form.getValues('account_id');
    const entityId = form.getValues('entity_id');
    const nmqEntityId = form.getValues('nmq_entity_id');
    const servicePeriodStart = form.getValues('service_period_start');
    const servicePeriodEnd = form.getValues('service_period_end');

    if (accountId && entityId && nmqEntityId && servicePeriodStart && servicePeriodEnd) {
      try {
        const csrfToken = getCsrfToken();
        const response = await axios.post(`/auto_invoices/get_invoices`, {
          account_id: [accountId],
          entity_id: [entityId],
          nmq_entity_id: [nmqEntityId],
          start_date: formatToISO(servicePeriodStart),
          end_date: formatToISO(servicePeriodEnd),
          status: 'APPROVED',
          type: ['MANUAL INVOICE', 'INVOICE'],
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        setCreditInvoiceList(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleInvoiceChange() {
    const response = await axios.get(`/auto_invoices/${form.getValues('auto_invoice_id')}/get_invoice`);

    setCreditInvoice(response.data as AutoInvoice);

    form.setValue('client_address', response.data.client_address);
    form.setValue('client_contact', response.data.client_contact);
    form.setValue('client_vat', response.data.client_vat);
    form.setValue('currency_type', response.data.currency_type);
    form.setValue('invoicing_queries', response.data.invoicing_queries);
    form.setValue('payment_terms', response.data.payment_terms);
    form.setValue('reference_no', response.data.reference_no);
    form.setValue('order_ref_no', response.data.order_ref_no);
    form.setValue('total_amount', response.data.total_amount);
    form.setValue('vat_amount', response.data.vat_amount);
    form.setValue('total_sub_amount', response.data.total_sub_amount);

    setHiddenValues((prevValues) => ({
      ...prevValues,
      exact_customer_id: response.data.exact_customer_id,
      client_country: response.data.client_country,
      vat_percent: calculateVatPercent(response.data as AutoInvoice),
      actual_total_amount: parseFloat(response.data.total_amount),
      total_amount: response.data.total_amount,
      vat_amount: response.data.vat_amount,
      total_sub_amount: response.data.total_sub_amount,
      calculated_data_html: generateTransformedHtml(
        response.data.calculated_data_html,
        response.data.unchecked_columns,
        response.data.additional_type,
        parseFloat(response.data.additional_cost_discount),
        response.data.exact_invoice_id
      ),
    }));
  }

  const formatNumber = (num: number, addCurrency: boolean = true) => {
    if (num === 0) return '';
    return `${addCurrency ? `${form.getValues('currency_type')} ` : ''}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)}`;
  };

  const generateTransformedHtml = (
    calculatedDataHtml: string,
    uncheckedColumns: string[],
    additional_type: string,
    additional_cost_discount: number,
    autoInvoiceText: string,
  ) => {
    const isOldData = !calculatedDataHtml.includes("Columns Settings");
    let transformedHtml;

    if (isOldData && calculatedDataHtml) {
      // Regex to transform the old HTML into a table format
  
      let rowsHtml = calculatedDataHtml
      .replace(/sowId/g, 'sowId p-2 checkbox-label')
      .replace(/servicePeriod/g, 'servicePeriod p-2 checkbox-label')
      .replace(/serviceType/g, 'serviceType p-2 checkbox-label')
      .replace(/rate/g, 'rate p-2 checkbox-label text-right')
      .replace(/description/g, 'description p-2 checkbox-label text-center')
      .replace(/hours/g, 'hours p-2 checkbox-label text-right')
      .replace(/subAmount/g, 'subAmount p-2 checkbox-label text-right')
      .replace(/totalAmount/g, 'totalAmount p-2 checkbox-label text-right');

      transformedHtml = `
        <table class="w-full border-collapse text-sm" style="table-layout: fixed;">
          <thead class="border-b-8 border-border">
            <tr>
              ${!uncheckedColumns.includes('sowId') ? `<th class="p-2 text-left min-w-36">Sow ID</th>` : ''}
              ${!uncheckedColumns.includes('servicePeriod') ? `<th class="p-2 text-left min-w-36">Service Period</th>` : ''}
              ${!uncheckedColumns.includes('serviceType') ? `<th class="p-2 text-left min-w-64">Service Type</th>` : ''}
              ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('rate') ? `<th class="p-2 text-right min-w-64">Rate</th>` : ''}
              ${!uncheckedColumns.includes('description') ? `<th class="p-2 text-center">Description</th>` : ''}
              ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('hours') ? `<th class="p-2 text-right min-w-64">Monthly Hours / Output Count</th>` : ''}
              ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('subAmount') ? `<th class="p-2 text-right min-w-36">Sub-Amount</th>` : ''}
              ${!uncheckedColumns.includes('totalAmount') ? `<th class="p-1 text-right min-w-36">Amount</th>` : ''}
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>`;
    } else {
      transformedHtml = calculatedDataHtml
        ?.replace(/<span>\s*on\s*<\/span>/g, '')
        .replace(/class="([^"]*?)"/g, (match: string, p1: string) => {
          const newClasses = p1
            .split(' ')
            .filter((cls: string) => cls !== 'cursor-pointer' && cls !== 'hover:bg-slate-300')
            .concat(['checkbox-label'])
            .join(' ');
          return `class="${newClasses}"`;
        })
        .replace(/<button(.*?)>/g, '<button$1 style="pointer-events: none; cursor: default;">');
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(transformedHtml, 'text/html');

    const subAmountTds = doc.querySelectorAll('td.subAmount');
    subAmountTds.forEach((td) => {
      if (!td.textContent?.trim().startsWith('- ')) {
        td.textContent = `- ${td.textContent?.trim()}`;
      }
    });

    const totalAmountTds = doc.querySelectorAll('td.totalAmount');
    totalAmountTds.forEach((td) => {
      if (!td.textContent?.trim().startsWith('- ')) {
        td.textContent = `- ${td.textContent?.trim()}`;
      }
    });

    if (additional_cost_discount !== 0) {
      const tbody = doc.querySelector('tbody');
      const newRow = doc.createElement('tr');
      newRow.classList.add('border-b-2 border-border'); 
      newRow.classList.add('checkbox-label');
      newRow.innerHTML = `
        <td colspan="${7-uncheckedColumns.length}">Invoice "${autoInvoiceText}" ${additional_type}</td>
        <td class="totalAmountAdditional text-right">${additional_type === 'ADDITIONAL CHARGE' ? '-' : ''} ${formatNumber(additional_cost_discount)}</td>
      `;
      tbody?.appendChild(newRow);
    }

    return doc.body.innerHTML;
  };

  const checkAmountsAndSubmit = (data: any) => {
    const { additional_cost_discount } = hiddenValues;

    if (additional_cost_discount !== 0) {
      setReasonType('credit note && amount differences');
    }

    setIsReasonModalOpen(true);
  };

  const handleReasonInput = (reason: string) => {
    setIsReasonModalOpen(false);
    form.handleSubmit((data) => handleSubmit({...data, reason: reason}))();
  };

  return (
    <Form {...form}>
      <ReasonModal
        isOpen={isReasonModalOpen}
        onOpenChange={setIsReasonModalOpen}
        reason_type={reasonType}
        onSubmitRequest={handleReasonInput}
      />
      <form
        onSubmit={form.handleSubmit(checkAmountsAndSubmit)}
        className="space-y-8 w-full"
      >
        
        {loading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <Spinner />
          </div>
        )}

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <DatePicker
                    placeholder="Date"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                    }}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="account_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Account*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.accounts.map((account) => ({
                      label: account.name,
                      value: account.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) => handleAccountChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                    enabledSearch={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="entity_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-2/6">
                <FormLabel>Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.entities.map((entity) => ({
                      label: entity.name,
                      value: entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) =>  handleEntityChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nmq_entity_id"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>NMQ Entity*</FormLabel>
                <FormControl>
                  <Select
                    options={formData.nmq_entities.map((nmq_entity) => ({
                      label: nmq_entity.name,
                      value: nmq_entity.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) =>  handleNMQEntityChange({ value, field, data: formData, setData: setFormData })}
                    disabled={mode == 'edit'}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="service_period_start"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Invoice Issue Month Start</FormLabel>
                <FormControl>
                  <DatePicker
                    placeholder="Range Start"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                      fetchInvoices();
                    }}
                    mode="month"
                    monthSelectionType="start"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service_period_end"
            render={({ field }) => (
              <FormItem className="flex flex-col w-1/6">
                <FormLabel>Invoice Issue Month End</FormLabel>
                <FormControl>
                  <DatePicker 
                    placeholder="Range End"
                    value={field.value ? toUtcDate(field.value) : undefined}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(toUtcDate(value, true));
                      }
                      fetchInvoices();
                    }}
                    mode="month"
                    monthSelectionType="end"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>


        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="auto_invoice_id"
            render={({ field }) => (
              <FormItem className="w-3/6">
                <FormLabel>Invoice to be created credit note</FormLabel>
                <FormControl>
                  <Select
                    options={creditInvoiceList.map((invoice) => ({
                      label: invoice.name,
                      value: invoice.id.toString(),
                    }))}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      handleInvoiceChange()
                    }}
                    disabled={!edit}
                    enabledSearch={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          
        </div>
        
        <div className="flex gap-12">

          <FormField
            control={form.control}
            name="reference_no"
            render={({ field }) => (
              <FormItem className="w-3/6">
                <FormLabel>Reference No</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Reference No"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="order_ref_no"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Order/PO Number</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Order/PO Number"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="payment_terms"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Payment Terms</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={true} placeholder= "Payment Terms"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="invoicing_queries"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Invoicing Queries</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={true} placeholder= "Invoicing Queries"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency_type"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Select
                    options={Constants.CURRENCIES.map((currency) => ({
                      label: currency,
                      value: currency,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="client_contact"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client Contact</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Client Contact"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="client_address"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client Address</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={true} placeholder= "Client Address"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="client_vat"
            render={({ field }) => (
              <FormItem className="w-1/5">
                <FormLabel>Client VAT</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={true} placeholder= "Client VAT"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-12">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="w-3/6">
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Notes"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="service_name"
            render={({ field }) => (
              <FormItem className="w-2/6">
                <FormLabel>Service Name</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} disabled={!edit} placeholder= "Service Name"/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-center px-12">
          {!loading && creditInvoice && (
            <div
              className="col-span-6 w-full overflow-auto"
              dangerouslySetInnerHTML={{ __html: hiddenValues.calculated_data_html }}
            />
          )}
        </div>
        
        <div className="flex justify-center px-12 !my-0">
        {!loading && creditInvoice && (
          <div className="w-full flex items-center justify-between ">
            <FormField
              control={form.control}
              name="additional_type"
              render={({ field }) => (
                <FormItem className="flex flex-col w-1/4">
                  <FormLabel></FormLabel>
                  <FormControl>
                    <Select
                      options={[
                        { label: "ADDITIONAL CHARGE", value: "ADDITIONAL CHARGE" }
                      ]}
                      value={field.value || "ADDITIONAL CHARGE"}
                      onChange={(value) => {
                        field.onChange(value);
                        setHiddenValues((prevValues) => ({
                          ...prevValues,
                          total_sub_amount:
                            creditInvoice.total_sub_amount +
                            (value === 'ADDITIONAL CHARGE'
                              ? +hiddenValues.additional_cost_discount
                              : -hiddenValues.additional_cost_discount),
                        }));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="additional_amount_description"
              render={({ field }) => (
                <FormItem className="flex flex-col w-1/4 ml-4">
                  <FormLabel></FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      disabled={!edit}
                      placeholder="Enter description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="additional_cost_discount"
              render={({ field }) => (
                <FormItem className="flex flex-col w-44 ml-4">
                  <FormLabel className="text-right"></FormLabel>
                  <FormControl className="">
                    <Controller
                      name="additional_cost_discount"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <ForwardedNumericFormat
                          {...controllerField}
                          value={controllerField.value || ""}
                          onValueChange={(values) => {
                            const { value } = values;
                            controllerField.onChange(value);
                            setHiddenValues((prevValues) => ({
                              ...prevValues,
                              total_sub_amount: creditInvoice.total_sub_amount - parseFloat(value),
                              additional_cost_discount: parseFloat(value),
                            }));
                          }}
                          thousandSeparator=","
                          decimalSeparator="."
                          fixedDecimalScale
                          decimalScale={2}
                          placeholder="0.00"
                          disabled={!edit}
                          className="border p-1 w-full text-right"
                          prefix={`${form.getValues('currency_type')} `}
                        />
                      )}
                    />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        )}
        </div>

        <div className="flex px-12">
          {!loading && creditInvoice && (
            <FormField
              control={form.control}
              name="total_sub_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">SubTotal Amount</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="total_sub_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.total_sub_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                              setHiddenValues((prevValues) => ({
                                ...prevValues,
                                vat_amount: (parseFloat(value) * hiddenValues.vat_percent) / 100,
                                total_amount: parseFloat(value) + (parseFloat(value) * hiddenValues.vat_percent) / 100,
                              }));
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={true}
                            className="border p-1 w-full text-right"
                            prefix={`- ${form.getValues('currency_type')} `}
                            allowNegative={false}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>


        <div className="flex justify-center px-12">
          {!loading && creditInvoice && (
            <FormField
              control={form.control}
              name="vat_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">{`VAT %${hiddenValues.vat_percent}`}</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="vat_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.vat_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={true}
                            className="border p-1 w-full text-right"
                            prefix={`- ${form.getValues('currency_type')} `}
                            allowNegative={false}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="flex justify-center px-12">
          {!loading && creditInvoice && (
            <FormField
              control={form.control}
              name="total_amount"
              render={({ field }) => (
                <FormItem className="flex  ml-auto items-end">
                  <div className="flex items-center w-full">
                    <FormLabel className="text-left w-48">Total Amount</FormLabel>
                    <FormControl className="w-44">
                      <Controller
                        name="total_amount"
                        control={form.control}
                        render={({ field: controllerField }) => (
                          <ForwardedNumericFormat
                            {...controllerField}
                            value={hiddenValues.total_amount}
                            onValueChange={(values) => {
                              const { value } = values;
                              controllerField.onChange(value);
                            }}
                            thousandSeparator=","
                            decimalSeparator="."
                            fixedDecimalScale
                            decimalScale={2}
                            placeholder="0.00"
                            disabled={true}
                            className="border p-1 w-full text-right"
                            prefix={`- ${form.getValues('currency_type')} `}
                            allowNegative={false}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <div className="flex gap-2 !mb-4">
          {mode === "create" && (
            <Link href="/auto_invoices">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit || loading}>
            {loading ? 'Loading...' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreditNoteForm;